/**
 * Daily sheet: the old Google Sheet's DAILY WORK tab as a screen. One line per loom that has a
 * saree on it, in loom order, with today's people filled in from last time.
 */
import { backfillBody, MoveStartOffer } from "../sarees/sareeEditModel";

export type PersonRef = { id: string; code: string; name?: string };

export type SheetLine = {
  key: string;
  loomId: string;
  loomCode: string;
  /** Loom register ID (LM-…), the loom's fixed identity. */
  registerId?: string;
  sareeNumber: string;
  designNumber?: string;
  weaverId: string;
  helperId: string; // "" = no helper today, see helperMode
  /** With no helper: the weaver alone (alone allowance) or no helper needed for this work (no allowance). */
  helperMode?: "ALONE" | "NOT_NEEDED";
  workTypeId: string;
  inches: string;
  /** Stable per line until saved, so a retry never saves twice. */
  requestId: string;
  /** Newest entry this screen saw on the loom that day ("" = none), so a save made meanwhile by someone else is caught. */
  afterEntryId?: string;
  status: "EDIT" | "SAVING" | "SAVED" | "ERROR";
  message?: string;
  amount?: string | null;
  /**
   * The loom has no saree on this day but one from a later day: saving this line also moves that
   * saree's start to this day (owner/manager), or asks for it with the entry (supervisor, via Approvals).
   */
  moveStart?: { pieceId: string; from: string };
};

export type SavedLine = {
  id: string;
  loomCode: string;
  workTypeCode: string;
  inches: string;
  weaverCode?: string;
  helperCode?: string | null;
  status: string;
  amount?: string | null;
};

export type IdleLoom = { id: string; code: string; registerId?: string; state: string; reason?: string };

let seq = 0;
export const newRequestId = (): string =>
  `sheet-${Date.now().toString(36)}-${(seq++).toString(36)}-${Math.random().toString(36).slice(2, 8)}`;

/** Split the rounds response into lines to fill, entries already saved that day, and looms with no saree. */
export const buildSheet = (rounds: any, defaultWorkTypeId: string) => {
  const lines: SheetLine[] = [];
  const saved: SavedLine[] = [];
  const idle: IdleLoom[] = [];
  for (const l of rounds?.looms || []) {
    let newest = "";
    for (const e of l.entries || []) {
      if (e.status === "VOID" || e.status === "REJECTED") continue;
      if (String(e.id) > newest) newest = String(e.id);
      saved.push({
        id: e.id,
        loomCode: l.code,
        workTypeCode: e.workTypeCode,
        inches: e.inches,
        weaverCode: e.weaverCode,
        helperCode: e.helperMode === "WITH_HELPER" ? e.helperCode : null,
        status: e.status,
        amount: e.calc?.total ?? null,
      });
    }
    const a = l.assignment;
    if (!a) {
      idle.push({ id: l.id, code: l.code, registerId: l.register?.loomCode, state: l.state, reason: l.closure?.reason });
      continue;
    }
    const last = l.lastPeople;
    const weaver: PersonRef | null = last?.weaver || a.weaver || null;
    const alone = last ? last.helperMode !== "WITH_HELPER" : a.helperMode !== "WITH_HELPER";
    const helper: PersonRef | null = alone ? null : last ? last.helper : a.helper;
    lines.push({
      key: l.id,
      loomId: l.id,
      loomCode: l.code,
      registerId: l.register?.loomCode,
      sareeNumber: a.sareeNumber,
      designNumber: a.designNumber,
      weaverId: weaver?.id || "",
      helperId: helper?.id || "",
      helperMode: (last ? last.helperMode : a.helperMode) === "NOT_NEEDED" ? "NOT_NEEDED" : "ALONE",
      workTypeId: l.lastWorkTypeId || defaultWorkTypeId,
      inches: "",
      requestId: newRequestId(),
      afterEntryId: newest,
      status: "EDIT",
    });
  }
  return { lines, saved, idle };
};

/** Lines ready to save: a number of inches and a weaver. */
export const readyToSave = (lines: SheetLine[]): SheetLine[] =>
  lines.filter((l) => l.status !== "SAVED" && l.status !== "SAVING" && Number(l.inches) > 0 && !!l.weaverId && !!l.workTypeId);

/** The request body for one line (the server works out pay). */
export const entryBody = (l: SheetLine, date: string) => ({
  loomId: l.loomId,
  date,
  workTypeId: l.workTypeId,
  inches: l.inches.trim(),
  unit: "INCH",
  weaverId: l.weaverId,
  helperId: l.helperId,
  // "Not needed" pays no alone allowance; sending "alone" for it would pay it by mistake.
  helperMode: l.helperId ? "WITH_HELPER" : l.helperMode === "NOT_NEEDED" ? "NOT_NEEDED" : "ALONE",
  clientRequestId: l.requestId,
  ...(l.afterEntryId !== undefined ? { afterEntryId: l.afterEntryId } : {}),
  // The server moves the start only if it is still `from` (or asks for approval), never half-applied.
  ...(l.moveStart ? backfillBody({ pieceId: l.moveStart.pieceId, currentStart: l.moveStart.from } as MoveStartOffer) : {}),
});

/**
 * Pasting a column copied from Google Sheets / Excel: "12\n10.5\n\n8" fills this line and the
 * ones below it. Returns null when the text is a single value (let the input handle it).
 */
export const pasteColumn = (lines: SheetLine[], fromIndex: number, text: string): SheetLine[] | null => {
  const values = text.replace(/\r/g, "").split("\n");
  while (values.length && values[values.length - 1].trim() === "") values.pop();
  if (values.length < 2) return null;
  const next = lines.slice();
  let row = fromIndex;
  for (const raw of values) {
    while (row < next.length && next[row].status === "SAVED") row++;
    if (row >= next.length) break;
    const v = raw.split("\t")[0].trim();
    next[row] = { ...next[row], inches: v, status: "EDIT", message: undefined };
    row++;
  }
  return next;
};
